import { Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import DropdownSelect from "components/DropdownSelect";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { usePermissionMonitor } from "components/PermissionMonitor";
import TranslatableTextField from "components/TranslatableTextField";
import { EXTERNAL, INTERNAL } from "const";
import { useAuth } from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import accountsService from "services/accounts-service";
import currenciesService from "services/currencies-service";
import regionsService from "services/regions-service";
import modulesService from "services/modules-service";

const auditLimitPerMonthList = Array.from({ length: 100 }, (_, i) => ({
  id: i + 1,
  label: i + 1,
}));

export default function SubscriptionDialog({
  isOpen,
  action,
  data,
  onSubmit,
  accountId,
}) {
  const { label } = useLanguage();
  const authContext = useAuth();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState({
    name: { en: "" },
    description: { en: "" },
    price: 0,
    extraAuditPrice: 0,
    type: authContext?.user?.account?.isExternalAuditor ? 2 : 0,
    maxAuditsMonth: -1,
    maxAuditsPeriod: -1,
    maxAuditsMysteryPeriod: -1,
    maxUsers: -1,
    maxSubAccounts: -1,
    maxBranchGroups: -1,
    period: 0,
    modules: [],
  });
  const [error, setError] = React.useState(null);
  const [options, setOptions] = React.useState({});
  const [dropdownOptions, setDropdownOptions] = React.useState({});
  const can = usePermissionMonitor();

  const fetchOptions = async () => {
    async function modules() {
      const [response, data] = await modulesService.getModules();
      if (response.ok) {
        setOptions((curr) => ({ ...curr, modules: data }));
      }
    }

    async function countries() {
      const [response, data] = await regionsService.getCountries();
      if (response.ok) {
        setOptions((curr) => ({ ...curr, countries: data }));
      }
    }

    async function currencies() {
      const [response, data] = await currenciesService.getCurrencies();
      if (response.ok) {
        setOptions((curr) => ({ ...curr, currencies: data }));
      }
    }

    async function accounts() {
      const [response, data] = await accountsService.getAccounts({
        raw: true,
        only: "externalAuditorsAndAvailable",
      });
      if (response.ok) {
        setOptions((curr) => ({ ...curr, accounts: data }));
      }
    }

    await Promise.all([modules(), countries(), currencies(), accounts()]);
  };

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setError(null);
  }, [isOpen]);

  React.useEffect(() => {
    if (action == "create") {
      setValue({
        name: { en: "" },
        description: { en: "" },
        price: 0,
        maxAuditsMonth: 0,
        extraAuditPrice: 0,
        maxAuditsPeriod: 0,
        maxAuditsMysteryPeriod: 0,
        type:
          dropdownOptions.types?.length === 1
            ? dropdownOptions.types?.[0]?.id
            : authContext?.user?.account?.isExternalAuditor
              ? 2
              : 0,
        periodDay: 0,
        periodMonth: 0,
        periodYear: 0,
        maxUsers: 0,
        maxSubAccounts: 0,
        maxBranchGroups: 0,
        period: 0,
        modules: [],
      });
    } else {
      setValue({
        ...data,
        name: data?.allLanguageName,
        description: data?.allLanguageDescription,
      });
    }
  }, [data, isOpen]);

  React.useEffect(() => {
    fetchOptions();
  }, []);

  React.useEffect(() => {
    setDropdownOptions({
      countries:
        options?.countries?.map((c) => ({ id: c.id, label: c.name })) || [],
      modules:
        options?.modules?.map((c) => ({ id: c.id, label: c.name })) || [],
      currencies:
        options?.currencies?.map((c) => ({
          id: c.id,
          label: c.name + " — " + c.value + "$",
        })) || [],
      accounts:
        options?.accounts?.map((a) => ({ id: a.id, label: a.name })) || [],
      types: [
        ...(can("isAdmin")
          ? [{ id: INTERNAL, label: label("Self-Service") }]
          : []),
        ...(authContext?.user?.account?.isExternalAuditor || can("isAdmin")
          ? [{ id: EXTERNAL, label: label("Third-Party") }]
          : []),
      ],
    });
  }, [options, authContext?.user, can("isAdmin")]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(value);
          if (result.success) {
            handleClose();
          } else {
            setError(result.error);
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle sx={{ mt: -0.5 }}>
        {action == "edit"
          ? label("edit-subscription")
          : label("create-subscription")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12}>
            <MDTypography variant="h6" sx={{ mt: -0.5 }}>
              {label("info")}
            </MDTypography>
          </Grid>
          <Grid item xs={12}>
            <TranslatableTextField
              autoFocus
              required
              error={error?.fields === "name"}
              helperText={error?.fields === "name" && error?.message}
              id="r"
              name="r"
              label={label("name-singular")}
              type="text"
              values={value.name || ""}
              onChange={(e) => {
                setValue({ ...value, name: e.target.value });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TranslatableTextField
              multiline
              required
              error={error?.fields === "description"}
              helperText={error?.fields === "description" && error?.message}
              id="r"
              name="r"
              label={label("description-singular")}
              type="text"
              values={value.description || ""}
              onChange={(e) => {
                setValue({ ...value, description: e.target.value });
              }}
              fullWidth
            />
          </Grid>

          {data?.type !== INTERNAL && value.type !== INTERNAL && !accountId && (
            <Grid item xs={12}>
              <DropdownSelect
                label={label("account-singular")}
                disabled={!value.type}
                inputProps={
                  !value.type
                    ? { helperText: label("select-a-type-first") }
                    : {}
                }
                initialValue={value.accountId || ""}
                onChange={(e) => {
                  setValue({ ...value, accountId: e?.id });
                }}
                items={dropdownOptions?.accounts}
                required={true}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <DropdownSelect
              multiple
              label={"Modules"}
              initialValue={value?.modules?.map((m) => m.id) || []}
              onChange={(e) => {
                setValue({ ...value, modules: e });
              }}
              items={dropdownOptions.modules || []}
              //required={true}
              fullWidth
            />
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <Grid container item xs={6} direction="column" spacing={1}>
              {dropdownOptions.types?.length === 2 ? (
                <Grid item xs={3}>
                  <DropdownSelect
                    label={label("type-singular")}
                    initialValue={value.type || ""}
                    onChange={(e) => {
                      setValue({ ...value, type: e?.id });
                    }}
                    items={dropdownOptions.types}
                    required={true}
                    fullWidth
                  />
                </Grid>
              ) : null}
              <Grid item xs={3}>
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.notifyExpiry}
                      value={value.notifyExpiry}
                      onChange={(e) => {
                        setValue({
                          ...value,
                          notifyExpiry: e.target.checked,
                        });
                      }}
                    />
                  }
                  label={label("notify-expiry-to-account-manager")}
                />
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <DropdownSelect
                multiple
                label={label("country-plural")}
                initialValue={
                  value?.countries?.map((country) => country.id) || []
                }
                onChange={(e) => {
                  setValue({ ...value, countries: e });
                }}
                items={dropdownOptions.countries || []}
                //required={true}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ mt: -1 }}>
            <Divider sx={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }} />
            <MDTypography variant="h6" sx={{ mt: -0.5 }}>
              {label("pricing-singular")}
            </MDTypography>
          </Grid>

          <Grid container item xs={12} spacing={1}>
            <Grid item xs={6}>
              <MDInput
                required
                margin="dense"
                id="r"
                name="r"
                label={label("price-singular")}
                min="0"
                step="0.01"
                type="number"
                error={error?.fields === "price"}
                helperText={error?.fields === "price" && error?.message}
                value={value.price}
                onChange={(e) => {
                  setValue({ ...value, price: Number(e.target.value) });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                required
                margin="dense"
                id="r"
                name="r"
                label={label("price-per-extra-audit")}
                min="0"
                step="0.01"
                type="number"
                error={error?.fields === "extraAuditPrice"}
                helperText={
                  error?.fields === "extraAuditPrice" && error?.message
                }
                value={value.extraAuditPrice}
                onChange={(e) => {
                  setValue({
                    ...value,
                    extraAuditPrice: Number(e.target.value),
                  });
                }}
                fullWidth
              />
            </Grid>
            {/* <Grid item xs={4}>
              <DropdownSelect
                label={"Currency"}
                initialValue={value.currencyId || ""}
                onChange={(e) => {
                  setValue({ ...value, currencyId: e?.id });
                }}
                renderOption={(option) => {
                  return option.label;
                }}
                items={dropdownOptions.currencies}
                required={true}
                fullWidth
              />
            </Grid>*/}
          </Grid>

          <Grid item xs={12} sx={{ mt: -1 }}>
            <Divider sx={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }} />
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <MDTypography variant="h6" sx={{ mt: -0.5 }}>
                {label("duration-singular")}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <UintTextField
              required
              margin="dense"
              error={error?.fields === "period"}
              helperText={error?.fields === "period" && error?.message}
              id="r"
              name="r"
              label={label("expires-after")}
              type="number"
              value={value.period}
              onChange={(e) => {
                setValue({ ...value, period: Number(e.target.value) || 0 });
              }}
              fullWidth
            />
          </Grid>

          {/*<Grid item xs={7}>
            <UintTextField
              required
              margin="dense"
              error={error?.fields === "maxAuditsMonth"}
              helperText={error?.fields === "maxAuditsMonth" && error?.message}
              id="r"
              name="r"
              label="Audit limit per month"
              type={value.maxAuditsMonth == -1 ? "text" : "number"}
              value={
                value.maxAuditsMonth == -1 ? "Unlimited" : value.maxAuditsMonth
              }
              disabled={value.maxAuditsMonth == -1}
              onChange={(e) => {
                setValue({
                  ...value,
                  maxAuditsMonth: Number(e.target.value) || 0,
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={5}>
            <FormControlLabel
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              control={
                <Checkbox
                  checked={value.maxAuditsMonth == -1}
                  value={value.maxAuditsMonth == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxAuditsMonth: !e.target.checked
                        ? value.maxAuditsMonth >= 0
                          ? Number(value.maxAuditsMonth)
                          : 0
                        : -1,
                    });
                  }}
                />
              }
              label="Unlimited audits per month"
            />
          </Grid>*/}

          <Grid item xs={12} sx={{ mt: -1 }}>
            <Divider sx={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }} />
            <MDTypography variant="h6" sx={{ mt: -0.5 }}>
              {label("limit-plural")}
            </MDTypography>
          </Grid>
          {value.maxAuditsPeriod == -1 &&
          (value.modules.find((v) => v.id == 1)
            ? value.maxAuditsMysteryPeriod == -1
            : true) ? null : (
            <>
              <Grid container item xs={12} spacing={1}>
                <Grid item xs={12}>
                  <MDTypography
                    variant="h6"
                    sx={{ mt: -0.5, fontWeight: "400", fontSize: "0.9rem" }}
                  >
                    {label("limit-period")}
                  </MDTypography>
                </Grid>

                <Grid item xs={4}>
                  <UintTextField
                    required
                    margin="dense"
                    error={error?.fields === "periodDay"}
                    helperText={error?.fields === "periodDay" && error?.message}
                    id="r"
                    name="r"
                    label={label("day-plural")}
                    type="number"
                    value={value.periodDay}
                    onChange={(e) => {
                      setValue({
                        ...value,
                        periodDay: Number(e.target.value) || 0,
                      });
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <UintTextField
                    required
                    margin="dense"
                    error={error?.fields === "periodMonth"}
                    helperText={
                      error?.fields === "periodMonth" && error?.message
                    }
                    id="r"
                    name="r"
                    label={label("month-plural")}
                    type="number"
                    value={value.periodMonth}
                    onChange={(e) => {
                      setValue({
                        ...value,
                        periodMonth: Number(e.target.value) || 0,
                      });
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <UintTextField
                    required
                    margin="dense"
                    error={error?.fields === "periodYear"}
                    helperText={
                      error?.fields === "periodYear" && error?.message
                    }
                    id="r"
                    name="r"
                    label={label("year-plural")}
                    type="number"
                    value={value.periodYear}
                    onChange={(e) => {
                      setValue({
                        ...value,
                        periodYear: Number(e.target.value) || 0,
                      });
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>{" "}
              <Grid item xs={12} mt={-1} mb={-1}>
                <Divider sx={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }} />
              </Grid>
            </>
          )}

          <Grid item xs={7}>
            <UintTextField
              required
              margin="dense"
              error={error?.fields === "maxAuditsPeriod"}
              helperText={error?.fields === "maxAuditsPeriod" && error?.message}
              id="r"
              name="r"
              label={
                <>
                  {label("audit-limit-every")}{" "}
                  {value.periodYear
                    ? `${value.periodYear} ${value.periodYear == 1 ? label("year-singular") : label("year-plural")}`
                    : ""}
                  {value.periodMonth
                    ? `${
                        value.periodYear
                          ? value.periodDay
                            ? ", "
                            : ` ${label("and")} `
                          : ""
                      }${value.periodMonth} ${value.periodYear == 1 ? label("month-singular") : label("month-plural")}`
                    : ""}
                  {value.periodDay
                    ? `${value.periodYear || value.periodMonth ? ` ${label("and")} ` : ""}${
                        value.periodDay
                      } ${value.periodYear == 1 ? label("day-singular") : label("day-plural")}`
                    : ""}
                </>
              }
              type={value.maxAuditsPeriod == -1 ? "text" : "number"}
              value={
                value.maxAuditsPeriod == -1
                  ? label("unlimited")
                  : value.maxAuditsPeriod
              }
              disabled={value.maxAuditsPeriod == -1}
              onChange={(e) => {
                setValue({
                  ...value,
                  maxAuditsPeriod: Number(e.target.value) || 0,
                });
              }}
              fullWidth
            />
          </Grid>

          <Grid item xs={5}>
            <FormControlLabel
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              control={
                <Checkbox
                  checked={value.maxAuditsPeriod == -1}
                  value={value.maxAuditsPeriod == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxAuditsPeriod: !e.target.checked
                        ? value.maxAuditsPeriod >= 0
                          ? Number(value.maxAuditsPeriod)
                          : 0
                        : -1,
                    });
                  }}
                />
              }
              label={label("unlimited-audits-per-period")}
            />
          </Grid>

          {value.modules.find((v) => v.id == 1) ? (
            <>
              <Grid item xs={7}>
                <UintTextField
                  required
                  margin="dense"
                  error={error?.fields === "maxAuditsMysteryPeriod"}
                  helperText={
                    error?.fields === "maxAuditsMysteryPeriod" && error?.message
                  }
                  id="r"
                  name="r"
                  label={
                    <>
                      {"Mystery audit limit every"}{" "}
                      {value.periodYear
                        ? `${value.periodYear} ${value.periodYear == 1 ? label("year-singular") : label("year-plural")}`
                        : ""}
                      {value.periodMonth
                        ? `${
                            value.periodYear
                              ? value.periodDay
                                ? ", "
                                : ` ${label("and")} `
                              : ""
                          }${value.periodMonth} ${value.periodYear == 1 ? label("month-singular") : label("month-plural")}`
                        : ""}
                      {value.periodDay
                        ? `${value.periodYear || value.periodMonth ? ` ${label("and")} ` : ""}${
                            value.periodDay
                          } ${value.periodYear == 1 ? label("day-singular") : label("day-plural")}`
                        : ""}
                    </>
                  }
                  type={value.maxAuditsMysteryPeriod == -1 ? "text" : "number"}
                  value={
                    value.maxAuditsMysteryPeriod == -1
                      ? label("unlimited")
                      : value.maxAuditsMysteryPeriod
                  }
                  disabled={value.maxAuditsMysteryPeriod == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxAuditsMysteryPeriod: Number(e.target.value) || 0,
                    });
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={5}>
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.maxAuditsMysteryPeriod == -1}
                      value={value.maxAuditsMysteryPeriod == -1}
                      onChange={(e) => {
                        setValue({
                          ...value,
                          maxAuditsMysteryPeriod: !e.target.checked
                            ? value.maxAuditsMysteryPeriod >= 0
                              ? Number(value.maxAuditsMysteryPeriod)
                              : 0
                            : -1,
                        });
                      }}
                    />
                  }
                  label={label("unlimited-audits-per-period")}
                />
              </Grid>
            </>
          ) : null}

          {value?.type !== EXTERNAL && (
            <>
              <Grid item xs={7}>
                <UintTextField
                  required
                  margin="dense"
                  error={error?.fields === "maxUsers"}
                  helperText={error?.fields === "maxUsers" && error?.message}
                  id="r"
                  name="r"
                  label={label("users-limit")}
                  type={value.maxUsers == -1 ? "text" : "number"}
                  value={value.maxUsers == -1 ? "Unlimited" : value.maxUsers}
                  disabled={value.maxUsers == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxUsers: Number(e.target.value) || 0,
                    });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={5}>
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.maxUsers == -1}
                      value={value.maxUsers == -1}
                      onChange={(e) => {
                        setValue({
                          ...value,
                          maxUsers: !e.target.checked
                            ? value.maxUsers >= 0
                              ? Number(value.maxUsers)
                              : 0
                            : -1,
                        });
                      }}
                    />
                  }
                  label={label("unlimited-users")}
                />
              </Grid>

              <Grid item xs={7}>
                <UintTextField
                  required
                  margin="dense"
                  error={error?.fields === "maxSubAccounts"}
                  helperText={
                    error?.fields === "maxSubAccounts" && error?.message
                  }
                  id="r"
                  name="r"
                  label={label("sub-accounts-limit")}
                  type={value.maxSubAccounts == -1 ? "text" : "number"}
                  value={
                    value.maxSubAccounts == -1
                      ? label("unlimited")
                      : value.maxSubAccounts
                  }
                  disabled={value.maxSubAccounts == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxSubAccounts: Number(e.target.value) || 0,
                    });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={5}>
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.maxSubAccounts == -1}
                      value={value.maxSubAccounts == -1}
                      onChange={(e) => {
                        setValue({
                          ...value,
                          maxSubAccounts: !e.target.checked
                            ? value.maxSubAccounts >= 0
                              ? Number(value.maxSubAccounts)
                              : 0
                            : -1,
                        });
                      }}
                    />
                  }
                  label={label("unlimited-sub-accounts")}
                />
              </Grid>

              <Grid item xs={7}>
                <UintTextField
                  required
                  margin="dense"
                  error={error?.fields === "maxBranchGroups"}
                  helperText={
                    error?.fields === "maxBranchGroups" && error?.message
                  }
                  id="r"
                  name="r"
                  label={label("branch-groups-limit")}
                  type={value.maxBranchGroups == -1 ? "text" : "number"}
                  value={
                    value.maxBranchGroups == -1
                      ? label("unlimited")
                      : value.maxBranchGroups
                  }
                  disabled={value.maxBranchGroups == -1}
                  onChange={(e) => {
                    setValue({
                      ...value,
                      maxBranchGroups: Number(e.target.value) || 0,
                    });
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={5}>
                <FormControlLabel
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  control={
                    <Checkbox
                      checked={value.maxBranchGroups == -1}
                      value={value.maxBranchGroups == -1}
                      onChange={(e) => {
                        setValue({
                          ...value,
                          maxBranchGroups: !e.target.checked
                            ? value.maxBranchGroups >= 0
                              ? Number(value.maxBranchGroups)
                              : 0
                            : -1,
                        });
                      }}
                    />
                  }
                  label={label("unlimited-branch-groups")}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {action == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function UintTextField({ onChange, ...props }) {
  return (
    <MDInput
      {...props}
      type="text"
      onChange={(e) => {
        if (!/^\d*$/.test(e.target.value)) return;

        if (onChange) {
          onChange(e);
        }
      }}
    />
  );
}
